import React, { createContext, useContext, useReducer, useEffect, useCallback, useMemo, ReactNode } from 'react'
import type { User, AuthContextType, AuthResponse, LoginCredentials, LoginOutcome } from '@/types'
import { authService } from '@services/authService'
import { widgetCache, CACHE_KEYS } from '@/utils/widgetCache'
import { AUTH_CHANGED_EVENT } from '@/utils/authEvents'
import { refreshAccessTokenWithLock, isRetryableRefreshError, SESSION_RETRY_MESSAGE } from '@/utils/apiUtils'
import toast from 'react-hot-toast'

// Auth State
interface AuthState {
  user: User | null
  token: string | null
  isLoading: boolean
}

// Auth Actions
type AuthAction =
  | { type: 'SET_LOADING'; payload: boolean }
  | { type: 'LOGIN_SUCCESS'; payload: { user: User; token: string } }
  | { type: 'LOGOUT' }
  | { type: 'UPDATE_USER'; payload: User }

// shallow 比對主要欄位，相同就保留原 reference 讓下游 useEffect 不會因 reference 抖動而重跑
const isSameUser = (a: User | null, b: User | null): boolean => {
  if (a === b) return true
  if (!a || !b) return false
  return (
    a.id === b.id &&
    a.email === b.email &&
    a.name === b.name &&
    a.role === b.role &&
    a.isActive === b.isActive &&
    a.level === b.level &&
    a.hourlyRate === b.hourlyRate &&
    a.avatar === b.avatar
  )
}

// Auth Reducer
const authReducer = (state: AuthState, action: AuthAction): AuthState => {
  switch (action.type) {
    case 'SET_LOADING':
      return { ...state, isLoading: action.payload }
    case 'LOGIN_SUCCESS': {
      // 換人登入 → 清掉前一個 user 的所有 widget cache（recentFavorites / projects 是 per-user）
      // 沒比對則同 user 重整時也會清，會抹掉剛 hydrate 的 state；故只在 id 真的不同時清
      const prev = widgetCache.get<User>(CACHE_KEYS.authUser)
      if (prev?.value && prev.value.id !== action.payload.user.id) {
        widgetCache.clearAll()
      }
      widgetCache.set(CACHE_KEYS.authUser, action.payload.user)

      // 若 incoming user 跟現有 state.user 內容相同，保留原 reference
      // 避免「啟動先 hydrate cache → /me 回來再 dispatch」造成 user reference 變動
      // → 所有 deps=[user] 的 useEffect 跑兩次 → API 第二輪 burst（Wave 2）
      const userToUse = isSameUser(state.user, action.payload.user)
        ? (state.user as User)
        : action.payload.user

      return {
        ...state,
        user: userToUse,
        token: action.payload.token,
        isLoading: false,
      }
    }
    case 'LOGOUT':
      widgetCache.clearAll()
      return {
        ...state,
        user: null,
        token: null,
        isLoading: false,
      }
    case 'UPDATE_USER': {
      widgetCache.set(CACHE_KEYS.authUser, action.payload)
      // 同上：內容沒變就保留 reference
      if (isSameUser(state.user, action.payload)) return state
      return { ...state, user: action.payload }
    }
    default:
      return state
  }
}

// Initial State — 啟動時直接 hydrate cache 的 user，UI 不用等 /me 才有資料
const initialToken = localStorage.getItem('token')
const cachedUser = widgetCache.get<User>(CACHE_KEYS.authUser)
// 沒 token 卻有舊 user cache → 視為過期，清掉避免短暫顯示假登入
if (!initialToken && cachedUser?.value) {
  widgetCache.invalidate(CACHE_KEYS.authUser)
}
const initialState: AuthState = {
  user: initialToken ? cachedUser?.value ?? null : null,
  token: initialToken,
  // 有 token + 有 cache → 樂觀渲染，背景驗證；其他狀況走 loading
  isLoading: !(cachedUser?.value && initialToken),
}

// Create Context
const AuthContext = createContext<AuthContextType | undefined>(undefined)

// Auth Provider Props
interface AuthProviderProps {
  children: ReactNode
}

// Auth Provider Component
export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [state, dispatch] = useReducer(authReducer, initialState)

  // 監聽 apiUtils 發出的強制登出事件
  useEffect(() => {
    const handleForceLogout = () => {
      dispatch({ type: 'LOGOUT' })
    }
    window.addEventListener('auth:forceLogout', handleForceLogout)
    return () => window.removeEventListener('auth:forceLogout', handleForceLogout)
  }, [])

  // Initialize auth state on mount
  useEffect(() => {
    const withTimeout = <T,>(promise: Promise<T>, ms: number): Promise<T> => {
      return Promise.race([
        promise,
        new Promise<never>((_, reject) =>
          setTimeout(() => reject(new Error('Request timeout')), ms)
        ),
      ])
    }

    const initializeAuth = async () => {
      const token = localStorage.getItem('token')
      const refreshToken = localStorage.getItem('refreshToken')

      if (token && refreshToken) {
        try {
          const user = await withTimeout(authService.getCurrentUser(), 15000)
          dispatch({ type: 'LOGIN_SUCCESS', payload: { user, token } })
        } catch (error: any) {
          console.error('Token verification failed:', error)

          // ⛔ 只有「明確的身分問題」才動 token。這裡的極性很重要：
          // 原本是「任何錯誤都當成 token 失效 → refresh → 失敗就清 token 登出」，
          // 但 429（限流）、逾時、網路異常都不是身分問題，token 其實還有效。
          // 尤其 withTimeout 拋出的是不帶 status 的 Error('Request timeout')——
          // getCurrentUser 內含 429 退避重試（1+2+4 秒），跨國連線很容易超過 15 秒上限，
          // 所以「被限流」最終會偽裝成逾時。若照舊往下走 refresh，只會再送一輪請求
          // 把限流桶填得更滿，最後被誤判成「refresh 也失敗」而清掉 token 把人踢回
          // 登入頁（本站登入要繞 SSO 中樞，代價很高）。
          // 因此改為白名單：只有 401/403 才 refresh，其餘一律保留登入狀態。
          const status = error?.status
          if (status !== 401 && status !== 403) {
            console.warn('非身分問題（限流／逾時／網路異常），保留 token 不登出:', error?.message)
            toast.error('連線不穩或伺服器忙碌，請稍後重新整理頁面')
            dispatch({ type: 'SET_LOADING', payload: false })
            return
          }

          // 確定是身分問題（401/403）才嘗試 refresh。
          // 續期會先向會員中樞換新的（後端 /auth/refresh），中樞暫時連不上（503）或逾時都不是
          // 「登入失效」，同上保留 token；只有後端明確說登入失效才清掉。
          // 共用 apiUtils 的那把鎖：跟頁面上其他 API 同時收到 401 時只會換一次。
          let newToken: string
          try {
            newToken = await withTimeout(refreshAccessTokenWithLock(), 20000)
          } catch (refreshError: any) {
            if (isRetryableRefreshError(refreshError) || refreshError?.message === 'Request timeout') {
              console.warn('續期暫時失敗（中樞或網路），保留 token 不登出:', refreshError?.message)
              toast.error(SESSION_RETRY_MESSAGE)
              dispatch({ type: 'SET_LOADING', payload: false })
              return
            }
            console.error('Token refresh failed:', refreshError)
            localStorage.removeItem('token')
            localStorage.removeItem('refreshToken')
            dispatch({ type: 'LOGOUT' })
            return
          }

          try {
            // Retry getting user info with new token
            const user = await withTimeout(authService.getCurrentUser(), 10000)
            dispatch({ type: 'LOGIN_SUCCESS', payload: { user, token: newToken } })
          } catch (retryError: any) {
            if (retryError?.status === 401 || retryError?.status === 403) {
              console.error('Token verification failed after refresh:', retryError)
              localStorage.removeItem('token')
              localStorage.removeItem('refreshToken')
              dispatch({ type: 'LOGOUT' })
              return
            }
            console.warn('續期成功但讀取使用者失敗，保留 token:', retryError?.message)
            toast.error('連線不穩或伺服器忙碌，請稍後重新整理頁面')
            dispatch({ type: 'SET_LOADING', payload: false })
          }
        }
      } else {
        // If either token is missing, clear both and set loading to false
        localStorage.removeItem('token')
        localStorage.removeItem('refreshToken')
        dispatch({ type: 'SET_LOADING', payload: false })
      }
    }

    initializeAuth()
  }, [])

  // 登入成功（帳密、Google、兩步驟驗證第二步共用）：存 token、更新狀態、通知外層 Provider
  const completeLogin = useCallback((response: AuthResponse): void => {
    // Store both tokens in localStorage
    localStorage.setItem('token', response.accessToken)
    localStorage.setItem('refreshToken', response.refreshToken)

    dispatch({
      type: 'LOGIN_SUCCESS',
      payload: { user: response.user, token: response.accessToken },
    })

    // SystemProvider 在 AuthProvider 外層拿不到登入狀態，靠這個事件重抓需授權的系統設定
    // （未登入時只拿得到軟體名稱，必填欄位清單會是空的）
    window.dispatchEvent(new Event(AUTH_CHANGED_EVENT))

    toast.success(`歡迎回來，${response.user.name}！`)

    // 用備用碼登入：提醒還剩幾組，快用完要到會員中樞重新產生
    if (typeof response.recoveryCodesRemaining === 'number') {
      toast(`已用備用碼登入，還剩 ${response.recoveryCodesRemaining} 組。快用完時請到會員中樞重新產生一組。`, {
        duration: 8000,
      })
    }
  }, [])

  // Login function：開了兩步驟驗證時回傳第二步的憑證（不算失敗、不跳錯誤提示），由登入頁接著問驗證碼
  const login = useCallback(async (credentials: LoginCredentials): Promise<LoginOutcome> => {
    try {
      dispatch({ type: 'SET_LOADING', payload: true })
      const result = await authService.login(credentials)
      if (result.status !== 'success') {
        dispatch({ type: 'SET_LOADING', payload: false })
        return result
      }
      completeLogin(result.auth)
      return { status: 'success' }
    } catch (error: any) {
      dispatch({ type: 'SET_LOADING', payload: false })
      const message = error.status === 403
        ? error.message
        : error.message || '登入失敗，請檢查您的帳號密碼'
      toast.error(message)
      throw error
    }
  }, [completeLogin])

  // Google login function（兩步驟驗證的處理同上）
  const googleLogin = useCallback(async (idToken: string): Promise<LoginOutcome> => {
    try {
      dispatch({ type: 'SET_LOADING', payload: true })
      const result = await authService.googleLogin(idToken)
      if (result.status !== 'success') {
        dispatch({ type: 'SET_LOADING', payload: false })
        return result
      }
      completeLogin(result.auth)
      return { status: 'success' }
    } catch (error: any) {
      dispatch({ type: 'SET_LOADING', payload: false })
      const message = error.status === 403
        ? error.message
        : error.message || 'Google 登入失敗，請稍後再試'
      toast.error(message)
      throw error
    }
  }, [completeLogin])

  // 兩步驟驗證第二步：不切全頁 loading（輸入框要留在畫面上、保住焦點），錯誤交給表單顯示
  const verifyMfa = useCallback(async (mfaToken: string, code: string): Promise<void> => {
    const response = await authService.verifyMfa(mfaToken, code)
    completeLogin(response)
  }, [completeLogin])

  // Logout function
  const logout = useCallback((): void => {
    try {
      const refreshToken = localStorage.getItem('refreshToken')

      // Remove both tokens from localStorage
      localStorage.removeItem('token')
      localStorage.removeItem('refreshToken')

      // Clear auth state
      dispatch({ type: 'LOGOUT' })

      // 讓外層 Provider 把只有登入才拿得到的設定清掉，避免殘留給下一位登入者
      window.dispatchEvent(new Event(AUTH_CHANGED_EVENT))

      toast.success('已成功登出')

      // 讓後端把這筆登入作廢（清掉存著的中樞 refresh token），不等結果、失敗也不影響登出
      if (refreshToken) {
        authService.revokeSession(refreshToken).catch(() => {})
      }
    } catch (error) {
      console.error('Logout error:', error)
    }
  }, [])

  // Update user function - 只更新本地狀態，不調用API
  const updateUser = useCallback((userData: User): void => {
    dispatch({ type: 'UPDATE_USER', payload: userData })
  }, [])

  // Context value
  const value: AuthContextType = useMemo(() => ({
    user: state.user,
    token: state.token,
    login,
    googleLogin,
    verifyMfa,
    logout,
    updateUser,
    isLoading: state.isLoading,
  }), [state.user, state.token, state.isLoading, login, googleLogin, verifyMfa, logout, updateUser])

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

// Custom hook to use auth context
export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext)
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
}

// HOC for components that require authentication
export const withAuth = <P extends object>(
  Component: React.ComponentType<P>
): React.FC<P> => {
  return (props: P) => {
    const { user, isLoading } = useAuth()

    if (isLoading) {
      return (
        <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-primary-50 to-primary-100">
          <div className="text-center">
            <div className="loading-spinner mx-auto mb-4"></div>
            <p className="text-secondary-600 font-medium">載入中...</p>
          </div>
        </div>
      )
    }

    if (!user) {
      return (
        <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-primary-50 to-primary-100">
          <div className="text-center">
            <h2 className="text-2xl font-bold text-secondary-800 mb-2">需要登入</h2>
            <p className="text-secondary-600">請先登入以使用此功能</p>
          </div>
        </div>
      )
    }

    return <Component {...props} />
  }
}

export default AuthContext
